import 'react-autocomplete-input/dist/bundle.css';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { IconButton } from 'src/components/Buttons';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { OperatorsIcon } from 'src/svgs/OperatorsIcon';
import {
  FORMULA_DISPLAY_ALLOWED_INSERT_CHARS,
  FORMULA_OPERATORS_AND_NUMBERS_REGEXP_START,
} from 'src/utils/columns/customColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { AGGR_FUNCTION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { Feature } from 'src/WebApiController';

import * as styles from '../../TableColumns.css';
import {
  AggregateFunction,
  CustomColumnType,
} from '../TableCustomColumnModal.types';

type FormulaBuilderInputProps = {
  /**
   * Key: display name, Value: column id
   * For example in listing: { 'Original': ListingTableColumnId.TicketCount }
   */
  fieldOptionsReverse: Record<string, string>;
  /**
   * Each token is a number literal, operator, or column id
   * For example in listing: ['ticketCount', '+', '20']
   *
   * In the UI it will show ["Original", "+", "20"]
   */
  formulaTokens: string[];
  setFormulaTokens: (tokens: string[]) => void;
  formulaCaretPosition: number;
  setFormulaCaretPosition: (position: number) => void;
  showMetricSelector: boolean;
  setShowMetricSelector: (show: boolean) => void;
  aggregateFunction?: AggregateFunction | undefined;
  setAggregateFunction?: (func: AggregateFunction) => void | undefined;
};

export function FormulaBuilderInput({
  fieldOptionsReverse,
  formulaTokens,
  setFormulaTokens,
  formulaCaretPosition,
  setFormulaCaretPosition,
  showMetricSelector,
  setShowMetricSelector,
  aggregateFunction,
  setAggregateFunction,
}: FormulaBuilderInputProps) {
  const hasAvgCntAggFeature = useUserHasFeature(Feature.AvgCntForReports);

  const fieldOptionsDisplay = useMemo(() => {
    const options: Record<string, string> = {};
    Object.keys(fieldOptionsReverse).forEach((key) => {
      options[fieldOptionsReverse[key]] = key;
    });
    return options;
  }, [fieldOptionsReverse]);

  const autoCompleteOptions = useMemo(
    () => Object.keys(fieldOptionsReverse),
    [fieldOptionsReverse]
  );

  const {
    formState: { errors },
    clearErrors,
  } = useFormContext<CustomColumnType>();

  const tokenRefs = useRef<HTMLSpanElement[]>([]);

  const formulaTokensWithCaret = useMemo(() => {
    if (formulaCaretPosition === -1) return formulaTokens;
    const result = [...formulaTokens];
    result.splice(formulaCaretPosition, 0, '|');
    return result;
  }, [formulaCaretPosition, formulaTokens]);

  const keyUpHandler = useCallback(
    (e: KeyboardEvent) => {
      // Keys are ignored if the editor is out of focus (no caret)
      if (formulaCaretPosition === -1) return;
      if (FORMULA_DISPLAY_ALLOWED_INSERT_CHARS.includes(e.key)) {
        if (
          // Number pressed and the previous token is number --> append
          /[0-9]/.test(e.key) &&
          formulaCaretPosition > 0 &&
          /[0-9]/.test(formulaTokens[formulaCaretPosition - 1])
        ) {
          const newTokens = [...formulaTokens];
          newTokens[formulaCaretPosition - 1] =
            formulaTokens[formulaCaretPosition - 1] + e.key;
          setFormulaTokens(newTokens);
        } else if (
          // Number pressed and the previous next is number --> prepend
          /[0-9]/.test(e.key) &&
          formulaCaretPosition < formulaTokens.length &&
          /[0-9]/.test(formulaTokens[formulaCaretPosition])
        ) {
          const newTokens = [...formulaTokens];
          newTokens[formulaCaretPosition] =
            e.key + formulaTokens[formulaCaretPosition];
          setFormulaTokens(newTokens);
        } else if (e.key === '@') {
          // @ --> open the metric selector dropdown
          setShowMetricSelector(true);
        } else {
          // else: replace the caret with the typed char (number or operator) and set new caret
          const newTokens = [...formulaTokensWithCaret].map((token) =>
            token === '|' ? e.key : token
          );
          setFormulaTokens(newTokens);
          setFormulaCaretPosition(formulaCaretPosition + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        // move caret left
        setFormulaCaretPosition(formulaCaretPosition - 1);
      } else if (e.key === 'ArrowRight') {
        // move caret right
        setFormulaCaretPosition(formulaCaretPosition + 1);
      } else if (e.key === 'Backspace') {
        // delete one token before the caret
        const newTokens = [...formulaTokens];
        newTokens.splice(formulaCaretPosition - 1, 1);
        setFormulaTokens(newTokens);
        setFormulaCaretPosition(formulaCaretPosition - 1);
      } else if (e.key === 'Delete') {
        // delete one token after the caret
        const newTokens = [...formulaTokens];
        newTokens.splice(formulaCaretPosition, 1);
        setFormulaTokens(newTokens);
      }
    },
    [
      formulaCaretPosition,
      formulaTokens,
      formulaTokensWithCaret,
      setFormulaCaretPosition,
      setFormulaTokens,
      setShowMetricSelector,
    ]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyUpHandler);

    return () => {
      window.removeEventListener('keyup', keyUpHandler);
    };
  }, [keyUpHandler]);

  useEffect(() => {
    // this ensures keeping refs up-to-date for click tracking & caret positioning
    tokenRefs.current = Array(formulaTokens.length).fill(null);
    clearErrors('formula');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaTokens]);

  return (
    <>
      <div className={styles.formulaTokenButtonsWrapper}>
        <PosDropdown
          trigger={
            <Button variant="text" style={{ height: '38px' }}>
              <div style={{ width: vars.iconSize.xl }}>
                <IconButton icon={<OperatorsIcon />}></IconButton>
              </div>
            </Button>
          }
          style={{
            display: 'block',
            margin: 'auto',
            padding: vars.spacing.sm,
          }}
        >
          {['+', '-', '*', '/', '(', ')'].map((label, i) => (
            <div
              key={i}
              className={styles.tokenButton}
              onClick={(e) => {
                e.stopPropagation();
                // add to tokens at caret position
                const newTokens = [...formulaTokensWithCaret].map((token) =>
                  token === '|' ? label : token
                );
                setFormulaTokens(newTokens);
                setFormulaCaretPosition(formulaCaretPosition + 1);
              }}
            >
              {label}
            </div>
          ))}
        </PosDropdown>
        <PosDropdown
          trigger={
            <Button variant="text">
              <Content id={ContentId.Metrics} />
            </Button>
          }
          style={{
            display: 'block',
            margin: 'auto',
            padding: vars.spacing.sm,
            width: '40%',
          }}
        >
          {autoCompleteOptions.map((label, i) => (
            <div
              key={i}
              className={styles.tokenButton}
              onClick={(e) => {
                e.stopPropagation();
                const tokenToAdd = fieldOptionsReverse[label] ?? '|';
                // add to tokens at caret position
                const newTokens = [...formulaTokensWithCaret].map((token) =>
                  token === '|' ? tokenToAdd : token
                );
                setFormulaTokens(newTokens);
                setFormulaCaretPosition(formulaCaretPosition + 1);
              }}
            >
              {label}
            </div>
          ))}
        </PosDropdown>
      </div>
      <PosFormField errors={errors.formula?.message}>
        <div className={styles.autoCompleteInputWrapper}>
          <div
            contentEditable={false}
            className={styles.formulaEditor}
            onClick={(e) => {
              e.stopPropagation();
              const tokenRowRange = formulaTokens
                .map((token, i) => ({
                  i,
                  token,
                }))
                .filter((tokenItem) => {
                  const tokenElement = tokenRefs.current[tokenItem.i];
                  if (!tokenElement) return false;
                  const rect = tokenElement.getBoundingClientRect();
                  return (
                    e.clientY >= rect.y && e.clientY <= rect.y + rect.height
                  );
                });
              if (tokenRowRange.length === 0) {
                setFormulaCaretPosition(formulaTokens.length);
                return;
              }
              const position = tokenRowRange.find((tokenItem) => {
                const tokenElement = tokenRefs.current[tokenItem.i];
                if (!tokenElement) return false;
                const rect = tokenElement.getBoundingClientRect();
                return rect.x > e.clientX;
              });
              if (position) {
                setFormulaCaretPosition(position.i);
              } else {
                // none of them were left of the click
                const lastInRow = tokenRowRange.at(-1) as {
                  i: number;
                  token: string;
                };
                setFormulaCaretPosition(lastInRow.i + 1);
              }
            }}
          >
            {formulaTokensWithCaret.map((token, i) =>
              token === '|' ? (
                showMetricSelector ? (
                  <PosEnumSelect
                    variant="textPlain"
                    shape="none"
                    rootProps={{
                      defaultOpen: true,
                    }}
                    onChange={(metric) => {
                      if (!metric) return;
                      const metricToken = fieldOptionsReverse[metric];
                      if (!metricToken) return;
                      const newTokens = [...formulaTokensWithCaret].map(
                        (token) => (token === '|' ? metricToken : token)
                      );
                      setFormulaTokens(newTokens);
                      setFormulaCaretPosition(formulaCaretPosition + 1);
                      setShowMetricSelector(false);
                    }}
                    searchable
                    valueOptionsContent={autoCompleteOptions.reduce(
                      (options, current) => ({
                        ...options,
                        [current]: current,
                      }),
                      {}
                    )}
                  />
                ) : (
                  <div key={i} className={styles.caret}>
                    |
                  </div>
                )
              ) : (
                <span
                  key={i}
                  ref={(el: HTMLSpanElement) => {
                    tokenRefs.current[i] = el;
                  }}
                  className={styles.formulaToken}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newCaretPosition =
                      formulaTokens.findIndex((t) => t === token) + 1;
                    setFormulaCaretPosition(newCaretPosition);
                  }}
                >
                  {FORMULA_OPERATORS_AND_NUMBERS_REGEXP_START.test(token)
                    ? token
                    : fieldOptionsDisplay[token]}
                </span>
              )
            )}
          </div>
          {hasAvgCntAggFeature && aggregateFunction && setAggregateFunction && (
            <PosEnumSelect
              value={aggregateFunction}
              onChange={(value) => {
                if (value) {
                  setAggregateFunction(value);
                }
              }}
              valueOptionsContent={AGGR_FUNCTION_TO_CID}
              style={{ textTransform: 'uppercase', width: '30%' }}
            />
          )}
        </div>
      </PosFormField>
    </>
  );
}
